import React, { useState, useEffect } from 'react';
import get from 'lodash/get';
import has from 'lodash/has';
import isObject from 'lodash/isObject';
import isFunction from 'lodash/isFunction';
import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import styled from 'styled-components';

import DraggableTable, { StyledPointerIconWrap } from 'src/presentation/components/pages/shared/DraggableTable';
import InformationIcon from '@ad-scripts/presentation/components/common/icons/fill/information-fill';
import InsetContainer from '@ad-scripts/presentation/components/common/containers/inset_container';
import Spinner from '@ad-scripts/presentation/components/common/spinner_two';
import theme from '@ad-scripts/presentation/themes/default';
import { Edit } from './rowActions/Edit';
import { OrderDirection } from 'src/presentation/components/pages/shared/utils/sortingUtils';
import { Table as ADTable, TBody, TD, TH, THead, TR } from '@ad-scripts/presentation/components/common/table';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';
import { isPrimitive } from 'src/presentation/components/pages/shared/utils/Utils';

export type SortingConfig =
  | {
      sortBy?: Function;
      direction?: OrderDirection;
    }
  | boolean;

type ColumnConfig = {
  attr: string;
  label?: string;
  // 1 - to add sorting for the column we can pass just `sorting: true`
  // it will handle common colums like Names, Counters etc
  // by default we sort in `asc` order

  // 2 - for complex cases like Dates, Statuses, etc we can provide SortingConfig object
  // it has two additional properties `direction` and `sortBy`
  // `direction` - allows start sorting from different orders (asc | desc)
  // `sortBy` -  allows customize sorting logic if we need it
  sorting?: SortingConfig;
  customRenderer?: (item: any) => JSX.Element | string;
  customColumnRender?: any;
  customColumnStyle?: any;
  customStyle?: (item: any) => Record<string, string>;
};

type TableProps = {
  className?: string;
  dataAid?: string;
  columnConfig: ColumnConfig[];
  items: any[];
  dragEndHandler?: Function;
  renderRowActions?: Function;
  tabRenderRow?: Function;
  isLoading: boolean;
  // `initialSorting` allows add initial sorting for table, it includes attr property which should be name of column
  //  also we can provide sorting direction. If direction is undefined we use asc as default direction
  initialSorting?: InitialSorting;
  pagination?: { pageSize: number; itemsTitle?: string };
  currentPage?: number;
  onChangeSorting?: Function;
  hideHeader?: boolean;
};

type SortTableBy = {
  attr?: string;
  direction: OrderDirection;
  sortBy?: Function;
  // optionally sort by a main column and sub columns and their directions.
  // if attr is defined, it takes precedence over attrsToDirections
  attrsToDirections?: Map<string, OrderDirection>;
};

export type InitialSorting = {
  attr?: string;
  direction?: OrderDirection;
  attrsToDirections?: Map<string, OrderDirection>;
};

const initialSortingParams: SortTableBy = {
  attr: undefined,
  direction: OrderDirection.asc,
  attrsToDirections: undefined,
  sortBy: undefined,
};

const getSortingIconState = (attr: string, sorting: SortingConfig, sortTableBy: SortTableBy) => {
  const isActive = sortTableBy.attr === attr;
  let downDirection = isObject(sorting) && sorting.direction === OrderDirection.desc;
  if (isActive) {
    downDirection = sortTableBy.direction === OrderDirection.desc;
  }
  return {
    downDirection,
    isVisible: sorting,
    isActive,
  };
};

const getOrderDirection = (initialSorting, columnSorting) => {
  return initialSorting.direction || columnSorting?.direction || OrderDirection.asc;
};

const getOrderDirectionByUserClick = (sortTableBy, attr, sorting) => {
  if (sortTableBy.attr === attr) {
    return toggleDirection(sortTableBy.direction);
  }
  return sorting.direction || OrderDirection.asc;
};

const toggleDirection = (direction: OrderDirection) =>
  direction === OrderDirection.asc ? OrderDirection.desc : OrderDirection.asc;

const Table = ({
  className,
  dataAid,
  columnConfig,
  items,
  dragEndHandler,
  renderRowActions,
  tabRenderRow,
  isLoading,
  initialSorting,
  pagination,
  currentPage,
  onChangeSorting,
  hideHeader,
}: TableProps): JSX.Element => {
  const [sortTableBy, saveSortingConfiguration] = useState(initialSortingParams);

  useEffect(() => {
    if (initialSorting && isUndefined(sortTableBy.attr)) {
      const columnItem = columnConfig?.find(({ attr }) => attr === initialSorting.attr);
      const sortBy = isObject(columnItem?.sorting) ? columnItem?.sorting?.sortBy : undefined;
      saveSortingConfiguration({
        attr: initialSorting.attr,
        direction: getOrderDirection(initialSorting, columnItem),
        attrsToDirections: initialSorting.attrsToDirections,
        sortBy,
      });
    }
  }, [initialSorting, columnConfig, sortTableBy.attr]);

  const updateOrder = (attr, sorting) => {
    if (!sorting) return;
    const direction = getOrderDirectionByUserClick(sortTableBy, attr, sorting);
    saveSortingConfiguration({ attr, direction, sortBy: sorting.sortBy });
    if (isFunction(onChangeSorting)) {
      onChangeSorting();
    }
  };

  const orderItems = items => {
    let records = items;
    if (sortTableBy.attr) {
      const sortingStrategy = sortTableBy.sortBy
        ? sortTableBy.sortBy
        : item => {
            const value = get(item, sortTableBy.attr || '');
            return typeof value !== 'string' ? value : value.toLowerCase();
          };
      records = orderBy(items, [sortingStrategy], [sortTableBy.direction]);
    } else if (sortTableBy.attrsToDirections) {
      const attrs: Function[] = Array.from(sortTableBy.attrsToDirections.keys()).map(
        attr => item => get(item, attr || '')
      );
      const directions: OrderDirection[] = Array.from(sortTableBy.attrsToDirections.values());
      records = orderBy(items, attrs, directions);
    }

    if (pagination && currentPage) {
      records = records.filter(
        (_, i) => i < currentPage * pagination.pageSize && i >= (currentPage - 1) * pagination.pageSize
      );
    }

    return records;
  };

  const headerCells = map(
    columnConfig,
    ({ attr, label, sorting = false, customColumnStyle = null, customColumnRender }) => {
      const labelText = customColumnRender ? customColumnRender(label) : label;
      return (
        <StyledTH
          data-aid={`${dataAid}-table-header-${attr}`}
          key={attr}
          style={customColumnStyle && customColumnStyle()}
        >
          {sorting ? (
            <StyledTHContainer
              data-aid={`table-header-${attr}-container`}
              onClick={() => updateOrder(attr, sorting)}
              sorting={sorting}
            >
              <div className="table-header-title">{labelText}</div>
              <StyledSortingIcon states={getSortingIconState(attr, sorting, sortTableBy)} />
            </StyledTHContainer>
          ) : (
            labelText
          )}
        </StyledTH>
      );
    }
  );

  dragEndHandler && headerCells.unshift(<StyledTH key="dragCol" />);
  headerCells.push(<StyledTH key="rowActions" />);

  function defaultRow(item) {
    return (
      <Row
        columnConfig={columnConfig}
        dataAid={dataAid}
        item={item}
        key={item.id}
        renderRowActions={renderRowActions}
      />
    );
  }

  const rowRenderer = tabRenderRow ? tabRenderRow : defaultRow;
  const sortedItems = orderItems(items);
  const rows: any[] = map(sortedItems, rowRenderer);

  const spinner = (
    <SpinnerContainer data-aid={`${dataAid}-loadingSpinner`}>
      <Spinner color={theme.colors.gray400} size={48} stroke={3} />
    </SpinnerContainer>
  );

  const tableBody = dragEndHandler ? (
    <DraggableTable rows={rows} dragEndHandler={dragEndHandler} />
  ) : (
    <TBody>{rows}</TBody>
  );

  const tableWrapper = (
    <>
      <TableWrapper isLoading={isLoading} className={className}>
        <StyledTable inset="medium" zebra data-aid={`${dataAid}-table`}>
          {!hideHeader && (
            <THead>
              <StyledHeaderTR>{headerCells}</StyledHeaderTR>
            </THead>
          )}
          {isLoading ? null : tableBody}
        </StyledTable>
      </TableWrapper>
      {isLoading ? spinner : null}
    </>
  );

  return tableWrapper;
};

const TableWrapper = styled.div`
  flex-shrink: ${p => (p.isLoading ? '0' : '1')};
  min-height: 0;
  overflow: auto;
`;

export const EmptyStateWrapper = styled(InsetContainer).attrs({ inset: 'large' })`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export type RowProps = {
  columnConfig: any;
  className?: string;
  rowDataAid?: string;
  dataAid?: string;
  hideActionsColumn?: boolean;
  item: any;
  renderRowActions?: Function;
  onClick?: Function;
};

type RowActionProps = {
  dataAid?: string;
  item: any;
};

export const defaultRenderRowActions = ({ item }: RowActionProps) => {
  return <Edit dataAid={`editLink-${item.id}`} id={item.id} />;
};

export const Row = (props: RowProps): JSX.Element => {
  const { columnConfig, className, rowDataAid, dataAid, item, renderRowActions, onClick } = props;
  const cells = map(columnConfig, ({ attr, defaultValue = '', customStyle = null }) => {
    let value = get(item, attr);
    const attrConfig = columnConfig.find(c => c.attr === attr);
    if (!value && attrConfig && attrConfig.defaultValue) {
      value = attrConfig.defaultValue;
    }

    if (has(attrConfig, 'customRenderer')) {
      value = attrConfig.customRenderer(item);
    }

    const tdDataAid = rowDataAid ? `${rowDataAid}-${attr}` : `${dataAid}-row-${item.id}-${attr}`;
    const tdTitle = isPrimitive(value) ? value : defaultValue;

    return (
      <StyledTD title={tdTitle} data-aid={tdDataAid} key={attr} style={customStyle && customStyle(item)}>
        {value || defaultValue}
      </StyledTD>
    );
  });

  const renderedRowActions = renderRowActions ? renderRowActions(props) : defaultRenderRowActions(props);

  const trDataAid = rowDataAid ? rowDataAid : `${dataAid}-row-${item.id}`;

  return (
    <StyledTR className={className} data-aid={trDataAid} key={item.id} onClick={onClick}>
      {cells}
      <TD style={{ width: '1%' }}>
        <RowActionContainer>{renderedRowActions}</RowActionContainer>
      </TD>
    </StyledTR>
  );
};

export const columnWithTooltip =
  ({ message, dataAid }) =>
  (label: string) =>
    (
      <ColumnLabelContainer data-aid={dataAid}>
        <ColumnLabel data-aid="column-label">{label}</ColumnLabel>
        <Tooltip
          data-aid="column-tooltip"
          message={<TooltipMessage data-aid="column-tooltip-text">{message}</TooltipMessage>}
        >
          <StyledInformationIcon />
        </Tooltip>
      </ColumnLabelContainer>
    );

const ColumnLabelContainer = styled.div`
  align-items: center;
  display: flex;
`;

const ColumnLabel = styled.span`
  margin-right: 6px;
  line-height: 162%;
`;

const TooltipMessage = styled.p`
  text-align: center;
  margin: 0;
  line-height: 162%;
`;

const StyledInformationIcon = styled(InformationIcon)`
  fill: ${p => p.theme.colors.gray600};
  height: 15px;
  width: 15px;
`;

const SpinnerContainer = styled.span`
  align-items: center;
  display: flex;
  flex: 1 1 300px;
  justify-content: center;
  width: 100%;
`;

const StyledHeaderTR = styled(TR)`
  padding: 0;
`;

const StyledTable = styled(ADTable)`
  border: 0;
  border-radius: 0;

  ${TH} {
    padding: 0 16px;
  }
`;

export const StyledTH = styled(TH)`
  height: 32px;
  padding: 0 16px;
  position: sticky;
  color: ${p => p.theme.colors.gray800};
  top: 0;
`;

export const RowActionContainer = styled.div`
  display: flex;
  * {
    margin: 0 0 0 5px;
  }
`;

export const StyledTR = styled(TR)`
  &&&:hover {
    background-color: ${p => p.theme.colors.green100};
    ${RowActionContainer} * {
      visibility: visible;
    }
    ${StyledPointerIconWrap} {
      visibility: visible;
    }
  }
`;

export const StyledTD = styled(TD)`
  /* max-width: 0 allows for a table cell to have ellipses for text overflow */
  max-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledSortingIcon = styled.div`
  display: none;
  position: absolute;
  top: 2px;
  bottom: 0;
  margin: auto;
  height: 12px;
  width: 12px;
  margin-left: 8px;
  vertical-align: middle;
  cursor: pointer;
  background: ${({ theme: { colors } }) => colors.gray800};
  transform: translate(0, 3px);
  opacity: 0;
  transition: 0.3s all;
  transform: translate(0, 3px);
  clip-path: polygon(50% 0%, 0 50%, 100% 50%);

  ${({ states: { isVisible } }) => isVisible && `{ display: inline-block; `}

  body &&& {
    ${({ states: { isActive } }) => isActive && ` { opacity: 1 `}
  }

  ${({ states: { downDirection } }) =>
    downDirection &&
    `{    
      transform: translate(0, -2px);
      clip-path: polygon(50% 100%, 0 50%, 100% 50%); `}
`;

const StyledTHContainer = styled.div`
  position: relative;
  cursor: ${({ sorting }) => (sorting ? 'pointer' : 'initial')};

  .table-header-title {
    display: inline-block;
  }

  &:hover ${StyledSortingIcon} {
    opacity: 0.6;
  }
`;
export default Table;
